import { supabase } from '@/integrations/supabase/client';
import {
  OnboardingProject,
  CreateProjectRequest,
  UpdateProjectRequest,
  ImportBatch,
  UploadImportRequest,
  FieldMappingRequest,
  ValidationResults,
  BillSource,
  CreateBillSourceRequest,
  GLCodeMapping,
  CreateGLCodeMappingRequest,
  Tag,
  TagAssignment,
  StageSignOff,
  SubmitSignOffRequest,
  OnboardingInsights,
  GoLiveRequest,
} from '@/types/onboarding';

export const onboardingApi = {
  // ============================================================================
  // Projects
  // ============================================================================
  async listProjects(workspaceId: string, filter?: 'in_progress' | 'completed' | 'archived'): Promise<{ projects: OnboardingProject[] }> {
    let query = supabase
      .from('tum_onboarding_projects')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    // Apply filter based on status category
    if (filter === 'archived') {
      query = query.eq('is_archived', true);
    } else if (filter === 'completed') {
      query = query.eq('is_archived', false).in('overall_status', ['approved', 'live']);
    } else if (filter === 'in_progress') {
      query = query.eq('is_archived', false).in('overall_status', ['draft', 'in_progress', 'pending_approval']);
    } else {
      // Default: show non-archived
      query = query.eq('is_archived', false);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { projects: (data || []) as unknown as OnboardingProject[] };
  },

  async getProject(projectId: string): Promise<OnboardingProject> {
    const { data, error } = await supabase
      .from('tum_onboarding_projects')
      .select('*')
      .eq('id', projectId)
      .single();

    if (error) throw error;
    if (!data) throw new Error('Project not found');
    return data as unknown as OnboardingProject;
  },

  async createProject(workspaceId: string, request: CreateProjectRequest): Promise<OnboardingProject> {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('tum_onboarding_projects')
      .insert({
        workspace_id: workspaceId,
        ...request,
        is_archived: false,
        created_by: user?.id,
        updated_by: user?.id,
      })
      .select()
      .single();

    if (error) throw error;

    // Initialize stage sign-offs
    const stages = ['data_import', 'validation', 'mapping', 'configuration', 'review', 'customer_signoff', 'go_live'];
    for (const stage of stages) {
      await supabase.from('tum_onboarding_stage_signoffs').insert({
        project_id: data.id,
        stage_name: stage,
        status: 'pending',
        required_signoff: true,
      });
    }

    return data as unknown as OnboardingProject;
  },

  async updateProject(projectId: string, request: UpdateProjectRequest): Promise<OnboardingProject> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const updateData: any = { ...request };
    if (request.customer_name && !request.project_name) {
      updateData.project_name = request.customer_name;
    }
    updateData.updated_by = user.id;

    const { data, error } = await supabase
      .from('tum_onboarding_projects')
      .update(updateData)
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('Project not found');
    return data as unknown as OnboardingProject;
  },

  // Archive/Unarchive/Delete projects
  async archiveProject(projectId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('tum_onboarding_projects')
      .update({
        is_archived: true,
        archived_at: new Date().toISOString(),
        archived_by: user?.id,
      })
      .eq('id', projectId);
    if (error) throw error;
  },

  async unarchiveProject(projectId: string): Promise<void> {
    const { error } = await supabase
      .from('tum_onboarding_projects')
      .update({
        is_archived: false,
        archived_at: null,
        archived_by: null,
      })
      .eq('id', projectId);
    if (error) throw error;
  },

  async deleteProject(projectId: string): Promise<void> {
    // Delete related data first (cascading)
    await supabase.from('tum_onboarding_staged_data')
      .delete()
      .in('batch_id', 
        supabase.from('tum_onboarding_import_batches')
          .select('id')
          .eq('project_id', projectId) as any
      );
    await supabase.from('tum_onboarding_import_batches').delete().eq('project_id', projectId);
    await supabase.from('tum_onboarding_stage_signoffs').delete().eq('project_id', projectId);
    
    const { error } = await supabase
      .from('tum_onboarding_projects')
      .delete()
      .eq('id', projectId);
    if (error) throw error;
  },

  // Upload customer logo
  async uploadCustomerLogo(projectId: string, file: File): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${projectId}/logo.${fileExt}`;
    
    const { error: uploadError } = await supabase.storage
      .from('customer-logos')
      .upload(fileName, file, { upsert: true });
    
    if (uploadError) throw uploadError;
    
    const { data: { publicUrl } } = supabase.storage
      .from('customer-logos')
      .getPublicUrl(fileName);
    
    // Update project with logo URL
    await supabase
      .from('tum_onboarding_projects')
      .update({ logo_url: publicUrl })
      .eq('id', projectId);
    
    return publicUrl;
  },

  // ============================================================================
  // Import Batches
  // ============================================================================
  async listImportBatches(projectId: string): Promise<ImportBatch[]> {
    const { data, error } = await supabase
      .from('tum_onboarding_import_batches')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as unknown as ImportBatch[];
  },

  async getExistingAccounts(workspaceId: string, customerName: string): Promise<any[]> {
    try {
      const { data: customers } = await supabase
        .from('customers')
        .select('id, name')
        .eq('workspace_id', workspaceId)
        .ilike('name', customerName)
        .limit(1);

      if (!customers || customers.length === 0) return [];

      const { data: accounts } = await supabase
        .from('accounts')
        .select('id, account_number, utility_type, supplier_name, service_address, customer_id')
        .eq('workspace_id', workspaceId)
        .eq('customer_id', customers[0].id);

      return accounts || [];
    } catch (error) {
      console.error('Error in getExistingAccounts:', error);
      return [];
    }
  },

  async checkDuplicateAccounts(
    workspaceId: string,
    customerName: string,
    stagedData: Array<{ row_number: number; mapped_data: Record<string, any> }>
  ): Promise<Array<{
    row_number: number;
    account_number: string;
    commodity_type: string;
    existing_account: any;
    new_data: any;
    is_same_commodity: boolean;
    resolution_type: 'error' | 'valid';
  }>> {
    const existingAccounts = await this.getExistingAccounts(workspaceId, customerName);
    const duplicates: Array<{
      row_number: number;
      account_number: string;
      commodity_type: string;
      existing_account: any;
      new_data: any;
      is_same_commodity: boolean;
      resolution_type: 'error' | 'valid';
    }> = [];

    for (const row of stagedData) {
      const accountNumber = row.mapped_data.account_number;
      const commodityType = row.mapped_data.utility_type || row.mapped_data.commodity_type;
      if (!accountNumber) continue;

      const existing = existingAccounts.find(
        (acc) => acc.account_number?.toLowerCase() === accountNumber.toLowerCase()
      );

      if (existing) {
        const existingCommodity = existing.utility_type || existing.commodity;
        const isSameCommodity = existingCommodity && commodityType && 
          existingCommodity.toLowerCase() === commodityType.toLowerCase();

        duplicates.push({
          row_number: row.row_number,
          account_number: accountNumber,
          commodity_type: commodityType || 'Unknown',
          existing_account: existing,
          new_data: row.mapped_data,
          is_same_commodity: isSameCommodity,
          resolution_type: isSameCommodity ? 'error' : 'valid',
        });
      }
    }

    return duplicates;
  },

  async uploadImportFile(
    projectId: string, 
    batchType: string, 
    file: File,
    options?: {
      field_mapping?: Record<string, string>;
      auto_create_vendors?: boolean;
      workspace_id?: string;
    }
  ): Promise<ImportBatch> {
    const { data: { user } } = await supabase.auth.getUser();

    const fileExt = file.name.split('.').pop();
    const fileName = `${projectId}/${batchType}/${Date.now()}.${fileExt}`;
    
    const { error: uploadError } = await supabase.storage
      .from('onboarding-imports')
      .upload(fileName, file);

    if (uploadError) throw uploadError;

    const { data, error } = await supabase
      .from('tum_onboarding_import_batches')
      .insert({
        project_id: projectId,
        batch_type: batchType,
        filename: file.name,
        file_url: fileName,
        file_size_bytes: file.size,
        status: 'uploaded',
        uploaded_by_user_id: user?.id || '',
      })
      .select()
      .single();

    if (error) throw error;

    // Auto-process file with optional mappings and vendor auto-creation
    await this.processImportFile(data.id, fileName, batchType, projectId, options);

    return data as unknown as ImportBatch;
  },

  async processImportFile(
    batchId: string, 
    filePath: string, 
    batchType: string, 
    projectId: string,
    options?: {
      field_mapping?: Record<string, string>;
      auto_create_vendors?: boolean;
      workspace_id?: string;
    }
  ): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(`https://dyftqspuiyabqzjslwyw.supabase.co/functions/v1/onboarding-process-file`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR5ZnRxc3B1aXlhYnF6anNsd3l3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM0MjA5MzIsImV4cCI6MjA2ODk5NjkzMn0.mibvMCccmDDhYCg7JY42pyc9dXjnYWWE32lVrv25V-U',
      },
      body: JSON.stringify({
        batch_id: batchId,
        file_path: filePath,
        batch_type: batchType,
        project_id: projectId,
        field_mapping: options?.field_mapping,
        auto_create_vendors: options?.auto_create_vendors,
        workspace_id: options?.workspace_id,
      }),
    });

    const responseText = await response.text();
    if (!response.ok) {
      let errorMessage = `Processing failed with status ${response.status}`;
      try {
        const errorBody = JSON.parse(responseText);
        errorMessage = errorBody.error || errorMessage;
      } catch {}
      throw new Error(errorMessage);
    }

    const result = JSON.parse(responseText);
    if (result.error) throw new Error(result.error);
  },

  async submitFieldMapping(batchId: string, request: FieldMappingRequest): Promise<ImportBatch> {
    const { data: batch } = await supabase
      .from('tum_onboarding_import_batches')
      .select('*')
      .eq('id', batchId)
      .single();

    if (!batch) throw new Error('Batch not found');

    const { data: updatedBatch, error: updateError } = await supabase
      .from('tum_onboarding_import_batches')
      .update({ field_mapping: request.field_mapping, status: 'mapping' })
      .eq('id', batchId)
      .select()
      .single();

    if (updateError) throw updateError;

    await this.processImportFile(batchId, batch.file_url, batch.batch_type, batch.project_id);

    return updatedBatch as unknown as ImportBatch;
  },

  async validateData(batchId: string): Promise<ValidationResults> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(`https://dyftqspuiyabqzjslwyw.supabase.co/functions/v1/onboarding-validate-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR5ZnRxc3B1aXlhYnF6anNsd3l3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM0MjA5MzIsImV4cCI6MjA2ODk5NjkzMn0.mibvMCccmDDhYCg7JY42pyc9dXjnYWWE32lVrv25V-U',
      },
      body: JSON.stringify({ batch_id: batchId }),
    });
    
    const responseText = await response.text();
    if (!response.ok) {
      throw new Error(`Validation failed: ${responseText}`);
    }

    const data = JSON.parse(responseText);
    if (data.error) throw new Error(data.error);
    return data as ValidationResults;
  },

  // ============================================================================
  // Staged Data Row Operations
  // ============================================================================
  async createStagedRow(batchId: string, data: Record<string, any>): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    // Get max row number
    const { data: maxRow } = await supabase
      .from('tum_onboarding_staged_data')
      .select('row_number')
      .eq('batch_id', batchId)
      .order('row_number', { ascending: false })
      .limit(1)
      .single();
    
    const nextRowNumber = (maxRow?.row_number || 0) + 1;
    
    const { error } = await supabase
      .from('tum_onboarding_staged_data')
      .insert({
        batch_id: batchId,
        row_number: nextRowNumber,
        source_data: data,
        mapped_data: data,
        validation_status: 'pending',
        last_edited_by: user?.id,
        last_edited_at: new Date().toISOString(),
      });
    
    if (error) throw error;
  },

  async deleteStagedRow(rowId: string): Promise<void> {
    const { error } = await supabase
      .from('tum_onboarding_staged_data')
      .delete()
      .eq('id', rowId);
    if (error) throw error;
  },

  async bulkDeleteStagedRows(rowIds: string[]): Promise<{ deleted: number; errors: string[] }> {
    // Batch size to avoid URL length limits (50 IDs per request)
    const BATCH_SIZE = 50;
    let deletedCount = 0;
    const errors: string[] = [];
    
    for (let i = 0; i < rowIds.length; i += BATCH_SIZE) {
      const batch = rowIds.slice(i, i + BATCH_SIZE);
      const { error } = await supabase
        .from('tum_onboarding_staged_data')
        .delete()
        .in('id', batch);
      
      if (error) {
        errors.push(`Batch ${Math.floor(i / BATCH_SIZE) + 1}: ${error.message}`);
      } else {
        deletedCount += batch.length;
      }
    }
    
    if (errors.length > 0 && deletedCount === 0) {
      throw new Error(errors.join('; '));
    }
    
    return { deleted: deletedCount, errors };
  },

  async updateStagedRow(rowId: string, data: Record<string, any>): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase
      .from('tum_onboarding_staged_data')
      .update({
        mapped_data: data,
        last_edited_by: user?.id,
        last_edited_at: new Date().toISOString(),
      })
      .eq('id', rowId);
    
    if (error) throw error;
  },

  // ============================================================================
  // Rest of the API (unchanged)
  // ============================================================================
  async executeImport(
    batchId: string,
    options: { skip_warnings?: boolean; skip_duplicates?: boolean; skip_errors?: boolean } = {}
  ): Promise<ImportBatch> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    await supabase
      .from('tum_onboarding_import_batches')
      .update({ status: 'importing' })
      .eq('id', batchId);

    const { data, error } = await supabase.functions.invoke('onboarding-execute-import', {
      body: { batch_id: batchId, ...options },
    });

    if (error) throw error;
    if (data.error) throw new Error(data.error);

    const { data: updatedBatch } = await supabase
      .from('tum_onboarding_import_batches')
      .select('*')
      .eq('id', batchId)
      .single();

    return updatedBatch as unknown as ImportBatch;
  },

  async getStagedData(batchId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('tum_onboarding_staged_data')
      .select('*')
      .eq('batch_id', batchId)
      .order('row_number', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async getImportedCounts(projectId: string): Promise<{ properties: number; units: number; accounts: number }> {
    try {
      // Get project to access workspace_id
      const { data: project, error: projectError } = await supabase
        .from('tum_onboarding_projects')
        .select('workspace_id, customer_name')
        .eq('id', projectId)
        .single();

      if (projectError || !project) {
        console.error('Project not found:', projectError);
        return { properties: 0, units: 0, accounts: 0 };
      }

      // CRITICAL FIX: Count ACTIVE properties/units/accounts from database tables
      // Only count active/accepted entities, exclude deleted ones
      // This ensures counts reflect only active entities that were accepted into the system
      
      // Count active properties (exclude deleted, only count active or null status)
      // Note: Properties default to 'active' status, so null is treated as active
      // Using .not() to explicitly exclude 'deleted' status
      const { count: propertiesCount, error: propertiesError } = await supabase
        .from('tum_properties')
        .select('id', { count: 'exact', head: true })
        .eq('workspace_id', project.workspace_id)
        .not('status', 'eq', 'deleted'); // Explicitly exclude deleted properties

      // Count active units (exclude deleted, include active statuses)
      const { count: unitsCount, error: unitsError } = await supabase
        .from('tum_units')
        .select('id', { count: 'exact', head: true })
        .eq('workspace_id', project.workspace_id)
        .not('status', 'eq', 'deleted'); // Explicitly exclude deleted units

      // Count active accounts (exclude deleted, include active)
      const { count: accountsCount, error: accountsError } = await supabase
        .from('tum_utility_accounts')
        .select('id', { count: 'exact', head: true })
        .eq('workspace_id', project.workspace_id)
        .not('status', 'eq', 'deleted'); // Explicitly exclude deleted accounts

      if (propertiesError) console.error('Error counting properties:', propertiesError);
      if (unitsError) console.error('Error counting units:', unitsError);
      if (accountsError) console.error('Error counting accounts:', accountsError);

      const counts = {
        properties: propertiesCount || 0,
        units: unitsCount || 0,
        accounts: accountsCount || 0,
      };

      console.log(`📊 Active entity counts for project ${projectId}:`, counts);

      // Auto-update estimated counts if they're less than actual counts
      // This ensures estimated counts are always >= actual active counts
      const { data: currentProject } = await supabase
        .from('tum_onboarding_projects')
        .select('estimated_properties, estimated_units, estimated_accounts')
        .eq('id', projectId)
        .single();

      if (currentProject) {
        const updates: any = {};
        if ((currentProject.estimated_properties || 0) < counts.properties) {
          updates.estimated_properties = counts.properties;
        }
        if ((currentProject.estimated_units || 0) < counts.units) {
          updates.estimated_units = counts.units;
        }
        if ((currentProject.estimated_accounts || 0) < counts.accounts) {
          updates.estimated_accounts = counts.accounts;
        }

        if (Object.keys(updates).length > 0) {
          console.log(`🔄 Auto-updating estimated counts:`, updates);
          await supabase
            .from('tum_onboarding_projects')
            .update(updates)
            .eq('id', projectId);
        }
      }

      return counts;
    } catch (error) {
      console.error('Error getting imported counts:', error);
      return { properties: 0, units: 0, accounts: 0 };
    }
  },

  async listBillSources(projectId: string): Promise<BillSource[]> {
    const { data, error } = await (supabase as any)
      .from('tum_onboarding_bill_sources')
      .select('*')
      .eq('project_id', projectId);
    if (error) throw error;
    return (data || []) as unknown as BillSource[];
  },

  async createBillSource(projectId: string, request: CreateBillSourceRequest): Promise<BillSource> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await (supabase as any)
      .from('tum_onboarding_bill_sources')
      .insert({ project_id: projectId, ...request, created_by: user?.id })
      .select()
      .single();
    if (error) throw error;
    return data as unknown as BillSource;
  },

  async listGLCodeMappings(projectId: string): Promise<GLCodeMapping[]> {
    const { data, error } = await (supabase as any)
      .from('tum_onboarding_gl_code_mappings')
      .select('*')
      .eq('project_id', projectId);
    if (error) throw error;
    return (data || []) as unknown as GLCodeMapping[];
  },

  async createGLCodeMapping(projectId: string, request: CreateGLCodeMappingRequest): Promise<GLCodeMapping> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await (supabase as any)
      .from('tum_onboarding_gl_code_mappings')
      .insert({ project_id: projectId, ...request, created_by: user?.id })
      .select()
      .single();
    if (error) throw error;
    return data as unknown as GLCodeMapping;
  },

  async listTags(projectId: string): Promise<Tag[]> {
    const { data, error } = await (supabase as any)
      .from('tum_onboarding_tags')
      .select('*')
      .eq('project_id', projectId);
    if (error) throw error;
    return (data || []) as unknown as Tag[];
  },

  async listTagAssignments(projectId: string): Promise<TagAssignment[]> {
    const { data, error } = await (supabase as any)
      .from('tum_onboarding_tag_assignments')
      .select('*')
      .eq('project_id', projectId);
    if (error) throw error;
    return (data || []) as unknown as TagAssignment[];
  },

  async getStageSignOffs(projectId: string): Promise<StageSignOff[]> {
    const { data, error } = await supabase
      .from('tum_onboarding_stage_signoffs')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data || []) as unknown as StageSignOff[];
  },

  async submitSignOff(signOffId: string, request: SubmitSignOffRequest): Promise<StageSignOff> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('tum_onboarding_stage_signoffs')
      .update({
        status: request.status,
        signed_by_user_id: user?.id,
        signed_at: new Date().toISOString(),
        comments: request.comments,
      })
      .eq('id', signOffId)
      .select()
      .single();
    if (error) throw error;
    return data as unknown as StageSignOff;
  },

  async getOnboardingInsights(projectId: string): Promise<OnboardingInsights | null> {
    const { data, error } = await supabase
      .from('tum_onboarding_insights')
      .select('*')
      .eq('project_id', projectId)
      .order('insight_date', { ascending: false })
      .limit(1)
      .maybeSingle(); // Use maybeSingle() instead of single() to handle missing rows gracefully
    if (error) {
      // Log error but don't throw - return null if table doesn't exist or RLS blocks access
      console.warn('Failed to load insights (this is OK if no insights exist yet):', error);
      return null;
    }
    return data as unknown as OnboardingInsights | null;
  },

  // Alias for getOnboardingInsights (used by useOnboardingInsights hook)
  async getInsights(projectId: string): Promise<OnboardingInsights | null> {
    return this.getOnboardingInsights(projectId);
  },

  // Customer invitation methods
  async getCustomerInvitation(token: string): Promise<any> {
    const { data, error } = await (supabase as any)
      .from('tum_onboarding_customer_invitations')
      .select('*, project:tum_onboarding_projects(*)')
      .eq('token', token)
      .single();
    if (error) throw error;
    return data;
  },

  async createCustomerInvitation(projectId: string, invitation: any): Promise<any> {
    const { data: { user } } = await supabase.auth.getUser();
    const token = crypto.randomUUID();
    const { data, error } = await (supabase as any)
      .from('tum_onboarding_customer_invitations')
      .insert({
        project_id: projectId,
        ...invitation,
        token,
        created_by: user?.id,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Account configuration methods
  async saveAccountConfiguration(projectId: string, accountId: string, config: any): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await (supabase as any)
      .from('onboarding_account_configurations')
      .upsert({
        project_id: projectId,
        staging_account_id: accountId,
        ...config,
        updated_by: user?.id,
      }, { onConflict: 'project_id,staging_account_id' });
    if (error) throw error;
  },

  // Activity log methods
  async getActivityLog(projectId: string): Promise<any[]> {
    const { data, error } = await (supabase as any)
      .from('tum_onboarding_activity_log')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    if (error) return [];
    return data || [];
  },

  // Flexible signature: (projectId, action, details?) OR (projectId, action, actionType, description, entityType, entityId)
  async logActivity(
    projectId: string, 
    action: string, 
    actionTypeOrDetails?: string | any,
    description?: string,
    entityType?: string,
    entityId?: string
  ): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    // Determine if using old 6-arg signature or new 3-arg signature
    let details: any;
    if (typeof actionTypeOrDetails === 'string' && description !== undefined) {
      // Old 6-arg signature: (projectId, action, actionType, description, entityType, entityId)
      details = {
        action_type: actionTypeOrDetails,
        description,
        entity_type: entityType,
        entity_id: entityId,
      };
    } else {
      // New 3-arg signature: (projectId, action, details?)
      details = actionTypeOrDetails;
    }
    
    await (supabase as any)
      .from('tum_onboarding_activity_log')
      .insert({
        project_id: projectId,
        action,
        details,
        user_id: user?.id,
      });
  },

  // Aliases for Stage4Mapping compatibility
  async listGLMappings(workspaceId: string): Promise<GLCodeMapping[]> {
    // This is a workspace-level query, not project-level
    const { data, error } = await (supabase as any)
      .from('tum_onboarding_gl_code_mappings')
      .select('*')
      .eq('workspace_id', workspaceId);
    if (error) throw error;
    return (data || []) as unknown as GLCodeMapping[];
  },

  async createBillSources(workspaceId: string, request: CreateBillSourceRequest): Promise<BillSource[]> {
    const { data: { user } } = await supabase.auth.getUser();
    const results: BillSource[] = [];
    
    // Create a bill source for each account_id
    for (const accountId of request.account_ids) {
      const { data, error } = await (supabase as any)
        .from('tum_onboarding_bill_sources')
        .insert({ 
          workspace_id: workspaceId, 
          account_id: accountId,
          source_type: request.source_type,
          ...request.config,
          created_by: user?.id 
        })
        .select()
        .single();
      if (error) throw error;
      results.push(data as unknown as BillSource);
    }
    
    return results;
  },

  async createGLMapping(workspaceId: string, request: CreateGLCodeMappingRequest): Promise<GLCodeMapping> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await (supabase as any)
      .from('tum_onboarding_gl_code_mappings')
      .insert({ workspace_id: workspaceId, ...request, created_by: user?.id })
      .select()
      .single();
    if (error) throw error;
    return data as unknown as GLCodeMapping;
  },

  async createTag(workspaceId: string, tag: Partial<Tag>): Promise<Tag> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await (supabase as any)
      .from('tum_onboarding_tags')
      .insert({ 
        workspace_id: workspaceId, 
        ...tag, 
        created_by: user?.id 
      })
      .select()
      .single();
    if (error) throw error;
    return data as unknown as Tag;
  },

  // Stage gate validation
  async validateStageGate(projectId: string, stage: string): Promise<{ valid: boolean; errors: string[] }> {
    // Basic validation - can be extended with specific stage rules
    try {
      const project = await this.getProject(projectId);
      const errors: string[] = [];
      
      // Add stage-specific validations here
      if (stage === 'data_import') {
        const batches = await this.listImportBatches(projectId);
        if (batches.length === 0) {
          errors.push('At least one data import batch is required');
        }
      }
      
      return { valid: errors.length === 0, errors };
    } catch {
      return { valid: false, errors: ['Failed to validate stage gate'] };
    }
  },

  async activateGoLive(projectId: string, request: GoLiveRequest): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('onboarding-activate', {
      body: { project_id: projectId, ...request },
    });

    if (error) throw error;
    if (data.error) throw new Error(data.error);
  },
};
